import { appConfigStore } from 'Models/AppConfigStore';
import { store } from 'Models/Store';
import React, { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { markBulkBookingsAsInvoiced } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { BookingOrderByOption } from 'Util/_HumanWritten/Bookings/FetchBookingsUtils';
import { BookingListExportDto } from 'Util/_HumanWritten/Bookings/FetchBookingsWithFilters';
import alertToast from 'Util/ToastifyUtils';
import { GenericStaffCollection } from 'Views/Components/_HumanWritten/StaffTables/GenericStaffCollection';
import { SortableHeaderButton } from 'Views/Components/_HumanWritten/StaffTables/SortableHeaderButton';
import { BookingColumnKeys } from 'Views/Components/_HumanWritten/StaffTables/StaffBookings/BookingColumnKeys';
import { BookingRender } from 'Views/Components/_HumanWritten/StaffTables/StaffBookings/BookingRender';
import { StaffBookingsFilters } from 'Views/Components/_HumanWritten/StaffTables/StaffBookings/StaffBookingsListOuter';
import { useStaffBookingsStore, isDynamicFieldColumnKey } from './StaffBookingsFilterStore';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import If from 'Views/Components/If/If';
import classnames from 'classnames';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';

export interface StaffBookingsCollectionProps {
	bookings: BookingListExportDto[] | null;
	totalBookingCount: number | undefined;
	perPage: number;
	filters: StaffBookingsFilters;
	setFilters: (newFilters: StaffBookingsFilters) => void;
	className?: string;
	isLoading: boolean;
	selectedBookingIds: Set<string>;
	setSelectedBookingIds: (ids: Set<string>) => void;
	lastUpdated: Date;
	refresh: () => void;
	hideCustomerColumn?: boolean;
}

export function StaffBookingsCollection({
	bookings,
	totalBookingCount,
	perPage,
	filters,
	setFilters,
	className,
	isLoading,
	setSelectedBookingIds,
	selectedBookingIds,
	lastUpdated,
	refresh,
	hideCustomerColumn,
}: StaffBookingsCollectionProps) {
	const intl = useIntl();
	const { visibleColumns: storeColumns } = useStaffBookingsStore();
	const visibleColumns = hideCustomerColumn
		? storeColumns.map(c => (c.key === BookingColumnKeys.CUSTOMER ? { ...c, visible: false } : c))
		: storeColumns;

	// Helper to check if column is visible
	const isColumnVisible = (key: string) => {
		return visibleColumns.find(col => col.key === key)?.visible ?? true;
	};

	const bookingRenders: ReactNode[] = [];

	if (bookings !== null) {
		bookings.forEach(booking => {
			const isChecked = selectedBookingIds.has(booking.id);
			bookingRenders.push(
				<BookingRender
					key={booking.id}
					booking={booking}
					isChecked={isChecked}
					onToggleCheckbox={() => {
						const newSet = new Set(selectedBookingIds);
						if (newSet.has(booking.id)) {
							newSet.delete(booking.id);
						} else {
							newSet.add(booking.id);
						}
						setSelectedBookingIds(newSet);
					}}
					visibleColumns={visibleColumns}
				/>,
			);
		});
	}

	const buttons = () => {
		return (
			<>
				<Button
					newStyles
					display={Display.Outline}
					colors={Colors.Secondary}
					onClick={() => {
						setSelectedBookingIds(new Set());
					}}
				>
					Clear selection
				</Button>
				<Button
					newStyles
					display={Display.Solid}
					colors={Colors.Secondary}
					onClick={async () => {
						const response = await markBulkBookingsAsInvoiced(Array.from(selectedBookingIds));
						if (response.data > 0) {
							alertToast(
								`Successfully set ${response.data}/${selectedBookingIds.size} booking${response.data > 1 ? 's' : ''} to invoiced`,
								'success',
							);
							setSelectedBookingIds(new Set());
							refresh();
						} else if (response.data === 0) {
							alertToast('Failed', 'error');
						} else {
							alertToast('No awaiting bookings were selected.', 'error');
						}
					}}
				>
					Set bookings to invoiced
				</Button>
			</>
		);
	};

	// Define all possible columns
	const allColumns = [
		{ key: 'checkbox', label: '' },
		{
			key: BookingColumnKeys.BOOKING_ID,
			label: 'Booking ID',
			headerComponent: (
				<SortableHeaderButton
					text="Booking ID"
					orderByField={BookingOrderByOption.BOOKING_ID}
					currentOrderBy={filters.orderBy}
					isDescending={filters.orderByDescending}
					onSort={(field, desc) =>
						setFilters({
							...filters,
							orderBy: field as BookingOrderByOption,
							orderByDescending: desc,
							pageNo: 0,
						})
					}
				/>
			),
		},
		{
			key: BookingColumnKeys.CREATED_DATE,
			label: 'Created on',
			headerComponent: (
				<SortableHeaderButton
					text="Created on"
					orderByField={BookingOrderByOption.CREATED_DATE}
					currentOrderBy={filters.orderBy}
					isDescending={filters.orderByDescending}
					onSort={(field, desc) =>
						setFilters({
							...filters,
							orderBy: field as BookingOrderByOption,
							orderByDescending: desc,
							pageNo: 0,
						})
					}
				/>
			),
		},
		...(store.isStaff ? [{ key: BookingColumnKeys.CREATED_BY, label: 'Created By' }] : []),
		...(store.isStaff ? [{ key: BookingColumnKeys.ALTERATION_COUNT, label: 'Times Altered' }] : []),
		...(store.isStaff ? [{ key: BookingColumnKeys.CUSTOMER, label: 'Customer' }] : []),
		{ key: BookingColumnKeys.TYPE, label: 'Type' },
		...(appConfigStore.eventsEnabled
			? [
					{ key: BookingColumnKeys.EVENT_NAME, label: 'Event Name' },
					{ key: BookingColumnKeys.EVENT_TICKETS, label: 'Event Tickets' },
					{ key: BookingColumnKeys.EVENT_PRIMARY_CONTACT, label: 'Event Contact' },
				]
			: []),
		{ key: BookingColumnKeys.DIRECTION, label: 'Direction' },
		{
			key: BookingColumnKeys.TRIP_DATE,
			label: 'Trip date',
			headerComponent: (
				<SortableHeaderButton
					text="Trip date"
					orderByField={BookingOrderByOption.TRIP_DATE}
					currentOrderBy={filters.orderBy}
					isDescending={filters.orderByDescending}
					onSort={(field, desc) =>
						setFilters({
							...filters,
							orderBy: field as BookingOrderByOption,
							orderByDescending: desc,
							pageNo: 0,
						})
					}
				/>
			),
		},
		...(store.isStaff ? [{ key: BookingColumnKeys.VESSEL, label: 'Vessel' }] : []),
		...(appConfigStore.vehicleEnabled
			? [
					{ key: BookingColumnKeys.VEHICLE_MAKE_MODEL, label: 'Vehicle Type' },
					{ key: BookingColumnKeys.VEHICLE_LENGTH, label: 'Vehicle Length' },
					{ key: BookingColumnKeys.VEHICLE_WEIGHT, label: 'Vehicle Weight' },
					...(appConfigStore.vehicleWidthEnabled
						? [{ key: BookingColumnKeys.VEHICLE_WIDTH, label: 'Vehicle Width' }]
						: []),
					{ key: BookingColumnKeys.VEHICLE_CHARACTERISTICS, label: 'Vehicle Characteristics' },
					{
						key: BookingColumnKeys.REGO,
						label: intl.formatMessage({
							id: 'vehicle-tab__rego__label',
							defaultMessage: 'Rego',
						}),
					},
					{ key: BookingColumnKeys.DRIVER_CONTACT, label: 'Driver Contact' },
				]
			: []),
		...(appConfigStore.trailersEnabled
			? [
					{ key: BookingColumnKeys.TRAILER_TYPE, label: 'Trailer Type' },
					{ key: BookingColumnKeys.TRAILER_LENGTH, label: 'Trailer Length' },
					{ key: BookingColumnKeys.TRAILER_WEIGHT, label: 'Trailer Weight' },
					...(appConfigStore.vehicleWidthEnabled
						? [{ key: BookingColumnKeys.TRAILER_WIDTH, label: 'Trailer Width' }]
						: []),
					{ key: BookingColumnKeys.TRAILER_CHARACTERISTICS, label: 'Trailer Characteristics' },
				]
			: []),
		{ key: BookingColumnKeys.PASSENGERS, label: 'Passengers' },
		...(appConfigStore.passengerInfoEnabled
			? [{ key: BookingColumnKeys.PASSENGER_NAMES, label: 'Passenger Names' }]
			: []),
		...(appConfigStore.passengerClassesEnabled
			? [
					{ key: BookingColumnKeys.PASSENGER_CLASSES, label: 'Fare Types' },
					{ key: BookingColumnKeys.SEATS, label: 'Seats' },
				]
			: []),
		...(appConfigStore.vehicleNoteEnabled || appConfigStore.passengerNoteEnabled
			? [{ key: BookingColumnKeys.ADDITIONAL_INFORMATION, label: 'Additional Information' }]
			: []),
		// One header per dynamic-field column (kept in the same order as the cells
		// rendered in BookingRender so columns and values line up).
		...visibleColumns
			.filter(col => isDynamicFieldColumnKey(col.key))
			.map(col => ({ key: col.key, label: col.label })),
		...(appConfigStore.freightEnabled
			? [
					{ key: BookingColumnKeys.FREIGHT_DESCRIPTION, label: 'Freight Description' },
					{ key: BookingColumnKeys.FREIGHT_DIMENSIONS, label: 'Freight Dimensions' },
				]
			: []),
		...(appConfigStore.freightEnabled && appConfigStore.freightReceiverDetailsEnabled
			? [{ key: BookingColumnKeys.FREIGHT_RECEIVER_DETAILS, label: 'Freight Receiver' }]
			: []),
		...(appConfigStore.freightEnabled && appConfigStore.freightNotifyDetailsEnabled
			? [{ key: BookingColumnKeys.FREIGHT_NOTIFY_DETAILS, label: 'Freight Notify' }]
			: []),
		{ key: BookingColumnKeys.ADD_ONS, label: 'Add Ons' },
		{ key: BookingColumnKeys.AMOUNT, label: 'Amount ($)' },
		{ key: BookingColumnKeys.TRAVEL_STATUS, label: 'Travel Status' },
		{ key: BookingColumnKeys.STATUS, label: 'Status' },
		...(appConfigStore.cancellationReasonsEnabled
			? [{ key: BookingColumnKeys.CANCELLATION_REASON, label: 'Cancellation Reason' }]
			: []),
		...(appConfigStore.systemWasMigrated ? [{ key: BookingColumnKeys.LEGACY_ID, label: 'Legacy ID' }] : []),
	];

	// Filter columns based on visibility
	const visibleColumnsForTable = allColumns.filter(col => isColumnVisible(col.key));

	return (
		<>
			<If condition={selectedBookingIds.size > 0 && store.isManager}>
				<div className="selected-booking-actions__container">
					<MessageBox
						title={`${selectedBookingIds.size} booking(s) selected`}
						status="info"
						actions={buttons()}
					>
						{
							<span>
								<strong>Note:</strong> Only awaiting bookings will be set to invoiced.
							</span>
						}
					</MessageBox>
				</div>
			</If>
			<GenericStaffCollection
				items={bookings}
				totalItemCount={totalBookingCount}
				perPage={perPage}
				currentPage={filters.pageNo}
				onPageChange={page => setFilters({ ...filters, pageNo: page })}
				isLoading={isLoading}
				selectedItemIds={selectedBookingIds}
				setSelectedItemIds={setSelectedBookingIds}
				lastUpdated={lastUpdated}
				className={classnames(className, 'staff__table')}
				renderRow={(booking, isChecked, toggle) => (
					<BookingRender
						booking={booking}
						isChecked={isChecked}
						onToggleCheckbox={toggle}
						visibleColumns={visibleColumns}
					/>
				)}
				columns={visibleColumnsForTable}
				emptyMessage="No bookings found"
				loadingMessage="Loading bookings"
			/>
		</>
	);
}
